import type { RefObject } from "react";
import type { ReleaseReason } from "@/api/client";
import { AudioRecorder } from "./AudioRecorder";
import { CopilotPanel } from "./CopilotPanel";
import {
  VisitCardBottomPanel,
  VisitCardErrorBoundary,
} from "./VisitCardBottomPanel";
import type { useVisitCard } from "../hooks/useVisitCard";
import type { useMeetingSession } from "../hooks/useMeetingSession";
import type { UseDemoMeetingPlaybackResult } from "../tour";
import { DEMO_VISIT_CARD_SESSION_ID } from "../tour/demoVisitCard";

type VisitCardApi = ReturnType<typeof useVisitCard>;
type MeetingSession = ReturnType<typeof useMeetingSession>;

export type MeetingBottomPanelProps = {
  featureFlags: {
    visit_card: boolean;
    copilot: boolean;
    checkpoints: boolean;
  };
  visitCard: VisitCardApi;
  meeting: MeetingSession;
  autoStart: string | null;
  selectedProfileId: string;
  selectedProfileTitle: string;
  launchAdoptSessionIdRef: RefObject<string | null>;
  onLaunchConfirmStarted: () => void;
  onLaunchFail: (reason: ReleaseReason) => void;
  registerTourTarget?: (id: string, el: HTMLElement | null) => void;
  demoPlayback?: UseDemoMeetingPlaybackResult | null;
};

export function MeetingBottomPanel({
  featureFlags,
  visitCard,
  meeting,
  autoStart,
  selectedProfileId,
  selectedProfileTitle,
  launchAdoptSessionIdRef,
  onLaunchConfirmStarted,
  onLaunchFail,
  registerTourTarget,
  demoPlayback,
}: MeetingBottomPanelProps) {
  const {
    copilotSessionId,
    isRecording,
    isCopilotActive,
    recommendations,
    checkpoints,
    showMissedCheckpoints,
    keepPanelAfterStop,
    copilotError,
    clearRecommendations,
    copilotSessionIdRef,
    onRecordingStart,
    onChunkReady,
    onRecorded,
    setIsRecording,
    setFinalizingRecord,
    startCopilot,
    stopCopilot,
    watchSessionDone,
    markLiveRecordingStopped,
    meetingProfileId,
    beginMeeting,
  } = meeting;

  const demoPlaying = Boolean(demoPlayback?.isPlaying);
  const demoKeepPanel = Boolean(demoPlayback?.keepPanel);
  const isOnMeetingProfile =
    meetingProfileId != null && selectedProfileId === meetingProfileId;
  const showCopilotPanel =
    isOnMeetingProfile &&
    featureFlags.copilot &&
    (isRecording ||
      isCopilotActive ||
      demoPlaying ||
      demoKeepPanel ||
      keepPanelAfterStop);
  const demoVisitCard = demoPlayback?.visitCard ?? null;
  const useDemoVisitCard =
    isOnMeetingProfile &&
    featureFlags.visit_card &&
    (demoPlaying || demoKeepPanel) &&
    Boolean(demoVisitCard) &&
    !copilotSessionId &&
    !visitCard.card;
  const showVisitCard =
    isOnMeetingProfile &&
    featureFlags.visit_card &&
    Boolean(visitCard.card || copilotSessionId || useDemoVisitCard);

  const handleClear = () => {
    clearRecommendations();
    demoPlayback?.dismissPanel();
  };

  const handleDemoScriptToggle = () => {
    if (!demoPlayback) return;
    if (demoPlayback.isPlaying) {
      demoPlayback.stop({ persist: true });
      return;
    }
    // Arm start: scripted meeting — no mic, no backend copilot.
    beginMeeting(selectedProfileId);
    demoPlayback.start();
    onLaunchConfirmStarted();
  };

  return (
    /* Bottom block (карточка + копилот + recorder) — НЕ sticky.
        Прижимается к низу через mt-auto когда контента мало,
        иначе уезжает вверх вместе со скроллом. */
    <div className="mt-auto pt-2">
      <div className="flex flex-col gap-2">
        {showVisitCard && (
          <VisitCardErrorBoundary>
            <VisitCardBottomPanel
              sessionId={
                useDemoVisitCard
                  ? DEMO_VISIT_CARD_SESSION_ID
                  : copilotSessionId
              }
              card={useDemoVisitCard ? demoVisitCard : visitCard.card}
              references={visitCard.references}
              referencesError={visitCard.referencesError}
              onRetryReferences={visitCard.retryReferences}
              flashes={
                useDemoVisitCard
                  ? (demoPlayback?.visitCardFlashes ?? [])
                  : visitCard.flashes
              }
              actionError={useDemoVisitCard ? null : visitCard.error}
              onClearActionError={visitCard.clearError}
              onPatch={visitCard.patchCard}
              onSubmit={useDemoVisitCard ? undefined : visitCard.submit}
              isSubmitting={useDemoVisitCard ? false : visitCard.isSubmitting}
              profileName={meetingProfileId ?? selectedProfileId}
              registerTourTarget={registerTourTarget}
            />
          </VisitCardErrorBoundary>
        )}
        {showCopilotPanel && (
          <CopilotPanel
            recommendations={recommendations}
            checkpoints={checkpoints}
            showCheckpoints={featureFlags.checkpoints}
            showMissedCheckpoints={showMissedCheckpoints}
            isActive={isCopilotActive || demoPlaying}
            isDemoRecording={demoPlaying}
            error={copilotError}
            onClear={handleClear}
            registerTourTarget={registerTourTarget}
          />
        )}
        <AudioRecorder
          autoStart={demoPlayback?.isArmed ? null : autoStart}
          demoScript={
            demoPlayback
              ? {
                  armed: demoPlayback.isArmed && !demoPlayback.isPlaying,
                  active: demoPlayback.isPlaying,
                  elapsedSec: demoPlayback.elapsedSec,
                }
              : null
          }
          onDemoScriptToggle={handleDemoScriptToggle}
          registerTourTarget={registerTourTarget}
          onRecordingStart={() => {
            // Live recording — dismiss any leftover demo panel state.
            demoPlayback?.dismissPanel();
            beginMeeting(selectedProfileId);
            onRecordingStart(selectedProfileId);
            setIsRecording(true);
            onLaunchConfirmStarted();
            if (featureFlags.copilot) {
              const adopt = launchAdoptSessionIdRef.current;
              if (adopt) {
                launchAdoptSessionIdRef.current = null;
                void startCopilot(adopt, { adopt: true });
              } else {
                void startCopilot(selectedProfileTitle);
              }
            }
          }}
          onChunk={onChunkReady}
          onRecordingComplete={(result: {
            blob: Blob;
            durationSec: number;
            totalChunks: number;
          }) => {
            onRecorded(result);
            setIsRecording(false);
            if (featureFlags.copilot) {
              markLiveRecordingStopped();
              const sessionId = copilotSessionIdRef.current;
              if (sessionId) {
                setFinalizingRecord(sessionId, true);
                watchSessionDone(sessionId);
              }
            }
          }}
          onRecordingError={() => {
            setIsRecording(false);
            onLaunchFail("recorder_failed");
            if (featureFlags.copilot) stopCopilot();
          }}
        />
      </div>
    </div>
  );
}
